/* The client half of the access gate. It decides nothing.
 *
 * accessGate.js used to hold the access codes, the master password and the key
 * that signed demo links, and compare them here in the browser. All three
 * shipped in the bundle, so anyone could read them out of the sources panel,
 * mint a demo link with any expiry they liked, or simply call the function that
 * opened the gate. It scoped a demo link; it was never a control.
 *
 * Now the server holds all of that. This module posts what the user typed,
 * gets back a signed session token or an error, and keeps the token. Every
 * request to the backend carries it, and the backend refuses anything without
 * one. What is in the bundle is this file, and there is nothing in it worth
 * reading.
 *
 * The token is kept in a module variable and nowhere else — not localStorage,
 * not sessionStorage, not a cookie. A reload signs the user in again. That is
 * the same lifetime the old in-memory gate had, and it keeps a bearer token for
 * a policy-review service out of any store an XSS or a shared machine can read.
 */

/* Microsoft Entra ID sign-in (from #12, made additive).
 *
 * A broker can sign in with their own Microsoft account: MSAL runs the
 * redirect and acquires an access token for this API (access_as_user), which
 * the backend verifies against Entra's keys. That sits alongside the sessions
 * below rather than replacing them — access codes stay as a fallback until
 * every broker has an Entra account, and the public trial needs a session
 * nobody signed in for. Entra is off unless config.js carries auth.clientId
 * and auth.tenantId (the web container's SP_AUTH_CLIENT_ID/SP_AUTH_TENANT_ID).
 *
 * MSAL keeps its own cache in sessionStorage (closing the tab signs out); the
 * service's own session token below still lives only in this module.
 */
import {
  EventType,
  InteractionRequiredAuthError,
  PublicClientApplication
} from '@azure/msal-browser';

const authCfg = () => ((typeof window !== 'undefined' && window.SENTRIPOL_CONFIG) || {}).auth || {};
export const entraConfigured = () => Boolean(authCfg().clientId && authCfg().tenantId);

let msal = null;
let account = null;

function msalClient() {
  if (msal) return msal;
  const { clientId, tenantId } = authCfg();
  msal = new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      // Also the post-logout target (MSAL's default), so only this one path
      // needs registering on the App Registration.
      redirectUri: '/auth/callback'
    },
    cache: { cacheLocation: 'sessionStorage' }
  });
  msal.addEventCallback((e) => {
    if (e.eventType === EventType.LOGIN_SUCCESS || e.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
      if (e.payload?.account) account = e.payload.account;
    }
  });
  return msal;
}

const entraScopes = () => [authCfg().apiScope || `api://${authCfg().clientId}/access_as_user`];

/** Finish a Microsoft sign-in redirect, or pick up an account from this tab's
 *  MSAL cache. Resolves to the session, or null when nobody is signed in with
 *  Microsoft (or Entra is not configured). */
export async function initEntra() {
  if (!entraConfigured()) return null;
  const app = msalClient();
  await app.initialize();
  const result = await app.handleRedirectPromise();
  account = result?.account || app.getActiveAccount() || app.getAllAccounts()[0] || null;
  if (!account) return null;
  app.setActiveAccount(account);
  const roles = account.idTokenClaims?.roles || [];
  current = {
    via: 'entra',
    label: account.username || account.name,
    name: account.name || account.username,
    role: roles.includes('admin') ? 'admin' : 'user',
    roles,
    expires_at: null
  };
  return current;
}

/** Start the Microsoft sign-in redirect. */
export function loginWithMicrosoft() {
  return msalClient().loginRedirect({ scopes: entraScopes() });
}

const base = () => {
  const b = ((typeof window !== 'undefined' && window.SENTRIPOL_CONFIG) || {}).backend;
  return (b == null ? '' : b).replace(/\/+$/, '');
};

let current = null; // { token, label, role, expires_at } — or, signed in with Microsoft, { via: 'entra', label, role, roles }

export const getSession = () => current;
/** The bearer token for the next request: a fresh Entra access token for a
 *  Microsoft sign-in (MSAL refreshes it silently), else the service's own
 *  session token. Async because the Entra path may refresh. */
export async function getToken() {
  if (!current) return null;
  if (current.via !== 'entra') return current.token;
  if (!account) return null;
  try {
    const res = await msalClient().acquireTokenSilent({ account, scopes: entraScopes() });
    return res.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      await msalClient().acquireTokenRedirect({ scopes: entraScopes() });
      return null; // navigating away
    }
    throw e;
  }
}
export const isAdmin = () => Boolean(current && current.role === 'admin');
/** A public trial session (from "See a live analysis" on sentripol.ai). The
 *  report is limited for these; the server limits what they can call. */
export const isTrial = () => Boolean(current && current.role === 'trial');
export const expiresAt = () => (current ? Date.parse(current.expires_at) : null);
export const isEntra = () => Boolean(current && current.via === 'entra');
export const clear = () => {
  current = null;
};

/** Sign out. A Microsoft sign-in also signs out of this app's Entra session
 *  (a redirect back to /); the service's own sessions just drop the token. */
export function logout() {
  const wasEntra = isEntra();
  current = null;
  if (wasEntra && msal) {
    const acct = msal.getActiveAccount() || account;
    account = null;
    return msal.logoutRedirect({ account: acct });
  }
  return Promise.resolve();
}

/** Authorization header for an authenticated call, or {} when signed out. */
export async function authHeader() {
  const token = await getToken();
  return token ? { Authorization: 'Bearer ' + token } : {};
}

async function post(path, body) {
  let res;
  try {
    res = await fetch(base() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
  } catch {
    // A network failure is not a rejected code, and saying "invalid access
    // code" when the server is simply down sends people hunting for the wrong
    // problem.
    throw new Error('Cannot reach the SentriPol service. Check your connection and try again.');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Sign-in failed (${res.status}).`);
  return data;
}

/** Redeem an access code. Resolves to the session, throws with the server's
 *  message on rejection. */
export async function redeemCode(code) {
  current = await post('/api/session', { code });
  return current;
}

/** Redeem the administrator password. */
export async function adminLogin(password) {
  current = await post('/api/session/admin', { password });
  return current;
}

/** Redeem a ?demo= token. The browser cannot read it — only the server that
 *  signed it can say what it means. */
export async function redeemDemoToken(token) {
  current = await post('/api/session/demo', { token });
  return current;
}

/** Start a public trial session. No code: the server decides whether trials
 *  are open and how many are left today, and says so if not. */
export async function startTrial() {
  current = await post('/api/session/trial', {});
  return current;
}

/** Mint a demo link. Admin sessions only; the server enforces that, this just
 *  asks. Returns the full URL to hand out. */
export async function createDemoLink(label, days) {
  let res;
  try {
    res = await fetch(base() + '/api/session/link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
      body: JSON.stringify({ label, days })
    });
  } catch {
    throw new Error('Cannot reach the SentriPol service.');
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Could not create the link (${res.status}).`);
  const url = new URL(window.location.href);
  url.searchParams.set('demo', data.token);
  return url.toString();
}
